import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, ChangeDetectorRef, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { EditorModule } from 'primeng/editor';
import { ModalComponent } from '../../../primeng/modal/modal.component';
import { UsecaseDetailComponent } from '../../../../../catalogue/usecase/detail/detail.component';
import { UsecasePreviewData } from '../../../../services/usecase-preview.service';

export interface UsecaseSection {
  id: string;
  description: string;
}

export interface ParsedSection {
  title: string;
  content?: string;
  subsections?: Array<{ subtitle: string; points: string[] }>;
}

@Component({
  selector: 'app-usecase-information',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    EditorModule,
    ModalComponent,
    UsecaseDetailComponent,
  ],
  templateUrl: './usecase-information.component.html',
  styleUrl: './usecase-information.component.scss',
})
export class UsecaseInformationComponent implements OnChanges {
  @Input() metadata: any = null; // Metadata from Step 1
  @Input() set sections(val: UsecaseSection[]) {
    this._sections = val && val.length > 0 ? [...val] : [];
  }
  @Output() sectionsChange = new EventEmitter<UsecaseSection[]>();
  @Output() parsedContentChange = new EventEmitter<ParsedSection[]>();
  @Output() validationErrorsChange = new EventEmitter<{ [key: string]: string }>();

  @ViewChild('validationError', { static: false }) validationErrorElement!: ElementRef<HTMLElement>;
  @ViewChild('previewContainer', { static: false }) previewContainer!: ElementRef<HTMLElement>;

  constructor(private cdr: ChangeDetectorRef) {}

  _sections: UsecaseSection[] = [];

  get sections(): UsecaseSection[] {
    return this._sections;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['sections'] && this._sections.length > 0) {
      this.modalSection.description = this._sections[0].description;
      this.parseEditorContent();
      this.updatePreviewData();
      this.parsedContentChange.emit([...this.parsedContent]);
    }
  }

  showModal: boolean = false;
  editingSection: UsecaseSection | null = null;
  modalSection = { description: '' };
  editorFormatFlaws: Array<{ index: number; element: string; message: string }> = [];
  canSaveContent = false;
  parsedContent: ParsedSection[] = [];
  previewDataForEmbedded: UsecasePreviewData | null = null;

  formatGuideExpanded = false;

  formatExamples = {
    h2a: 'Introduction',
    para: 'Across each procurement season, Paddy Procurement Centres handle significant volumes...',
    h2b: 'Current Opportunities for Improvement',
    h3: 'Limited foresight',
    bullet: 'Arrival patterns and mill capacities are understood only after trucks roll...'
  };

  private readonly formatExampleOptions = {
    h2a: [
      'Introduction', 'Overview', 'Background', 'Context', 'Summary', 'Key Findings',
      'Problem Statement', 'Solution Approach', 'Methodology', 'Results'
    ],
    para: [
      'Across each procurement season, Paddy Procurement Centres handle significant volumes...',
      'Farmers face several challenges when bringing produce to market...',
      'The current process involves multiple steps and stakeholders...',
      'Data from various sources can help improve decision-making...',
      'Understanding local patterns is essential for effective planning...',
      'Stakeholder feedback indicates a need for streamlined workflows...',
      'Analysis of regional trends reveals opportunities for optimization...',
      'Integration with existing systems requires careful consideration...'
    ],
    h2b: [
      'Current Opportunities for Improvement', 'Challenges and Constraints', 'Proposed Solutions',
      'Next Steps', 'Recommendations', 'Key Insights', 'Critical Success Factors',
      'Implementation Roadmap', 'Risk Assessment', 'Expected Outcomes'
    ],
    h3: [
      'Limited foresight', 'Key points', 'Main findings', 'Critical factors',
      'Implementation notes', 'Technical considerations', 'User requirements',
      'Data sources', 'Validation approach', 'Scope and limitations'
    ],
    bullet: [
      'Arrival patterns and mill capacities are understood only after trucks roll...',
      'Manual coordination leads to delays and inefficiencies...',
      'Real-time visibility would enable better resource allocation...',
      'Historical data can inform predictive analytics...',
      'Feedback loops help refine the approach over time...',
      'Stakeholder alignment is crucial for successful adoption...',
      'Automation can reduce manual effort by up to 40%...',
      'Pilot testing in selected regions will validate the solution...'
    ]
  };

  private pickRandomExamples(): void {
    this.formatExamples = {
      h2a: this.formatExampleOptions.h2a[Math.floor(Math.random() * this.formatExampleOptions.h2a.length)],
      para: this.formatExampleOptions.para[Math.floor(Math.random() * this.formatExampleOptions.para.length)],
      h2b: this.formatExampleOptions.h2b[Math.floor(Math.random() * this.formatExampleOptions.h2b.length)],
      h3: this.formatExampleOptions.h3[Math.floor(Math.random() * this.formatExampleOptions.h3.length)],
      bullet: this.formatExampleOptions.bullet[Math.floor(Math.random() * this.formatExampleOptions.bullet.length)]
    };
  }

  toggleFormatGuide(): void {
    this.formatGuideExpanded = !this.formatGuideExpanded;
    if (this.formatGuideExpanded) {
      this.pickRandomExamples();
    }
  }

  editorConfig = {
    modules: {
      toolbar: {
        container: [
          [{ 'header': [false, 2, 3] }],
          ['bold'],
          [{ 'list': 'bullet' }],
          ['clean']
        ],
        handlers: {}
      }
    }
  };

  private quillInstance: any = null;
  private isConvertingLists: boolean = false;

  hasError(field: string): boolean {
    return field === 'sections' && this.sections.length === 0;
  }

  getError(field: string): string {
    if (field === 'sections' && this.sections.length === 0) {
      return 'At least one section is required';
    }
    return '';
  }

  openAddModal(): void {
    if (this.sections && this.sections.length > 0) {
      const firstSection = this.sections[0];
      this.editingSection = firstSection;
      this.modalSection = { description: firstSection.description || '' };
      if (firstSection.description) {
        this.editorFormatFlaws = [];
      }
    } else {
      this.editingSection = null;
      this.modalSection = { description: '' };
      this.editorFormatFlaws = [];
    }
    this.pickRandomExamples();
    this.updateCanSaveContent();
    this.showModal = true;
  }

  closeModal(): void {
    this.showModal = false;
    this.editingSection = null;
    this.editorFormatFlaws = [];
    this.canSaveContent = false;
  }

  saveSection(): void {
    if (!this.modalSection.description.trim()) {
      return;
    }

    const flaws = this.findFormatFlaws(this.modalSection.description);
    if (flaws.length > 0) {
      this.editorFormatFlaws = flaws;
      this.cdr.detectChanges();
      setTimeout(() => {
        if (this.validationErrorElement?.nativeElement) {
          this.validationErrorElement.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
      return;
    }

    this.editorFormatFlaws = [];
    let sanitizedDescription = this.convertOrderedToUnorderedLists(this.modalSection.description);

    if (this.editingSection) {
      const index = this.sections.findIndex(s => s.id === this.editingSection!.id);
      if (index !== -1) {
        this.sections[index] = { ...this.sections[index], description: sanitizedDescription };
      }
    } else {
      const newSection: UsecaseSection = {
        id: 'section-' + Date.now(),
        description: sanitizedDescription
      };
      this.sections.push(newSection);
    }

    // Parse from modalSection.description (still has the value before modal closes)
    this.parseEditorContent();
    this.updatePreviewData();
    this.sectionsChange.emit([...this._sections]);
    this.parsedContentChange.emit([...this.parsedContent]);
    this.cdr.detectChanges();

    this.closeModal();

    // Scroll preview into view after modal closes so user sees their saved content
    setTimeout(() => {
      this.previewContainer?.nativeElement?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 150);
  }

  convertOrderedToUnorderedLists(html: string): string {
    if (!html) return html;
    
    let converted = html.replace(/list="ordered"/g, 'list="bullet"');
    converted = converted.replace(/list:ordered/g, 'list:bullet');
    
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = converted;
    
    const orderedLists = tempDiv.querySelectorAll('ol');
    orderedLists.forEach(ol => {
      const ul = document.createElement('ul');
      ul.innerHTML = ol.innerHTML;
      Array.from(ol.attributes).forEach(attr => {
        if (attr.name !== 'data-list' && attr.name !== 'class') {
          ul.setAttribute(attr.name, attr.value);
        } else if (attr.name === 'class') {
          const classes = attr.value.split(' ').filter((c: string) => 
            !c.includes('ql-list') && !c.includes('ordered')
          );
          if (classes.length > 0) {
            ul.setAttribute('class', classes.join(' '));
          }
        }
      });
      ol.parentNode?.replaceChild(ul, ol);
    });
    
    const listItems = tempDiv.querySelectorAll('li[data-list="ordered"]');
    listItems.forEach(li => li.setAttribute('data-list', 'bullet'));
    
    const listItemsWithClass = tempDiv.querySelectorAll('li.ql-list-ordered, li[class*="ordered"]');
    listItemsWithClass.forEach(li => {
      const classList = Array.from(li.classList);
      classList.forEach(cls => {
        if (cls.includes('ordered')) {
          li.classList.remove(cls);
          li.classList.add(cls.replace('ordered', 'bullet'));
        }
      });
    });
    
    return tempDiv.innerHTML;
  }

  parseEditorContent(): void {
    // Full parsing logic copied from parent component
    // Parse editor content: Supports two Usecases
    // Use Case 1: Heading 2 + Normal paragraphs → { title: '', content: '' }
    // Use Case 2: Heading 2 + Heading 1(s) + Bullets/Paragraphs → { title: '', subsections: [...] }
    if (!this.modalSection.description || !this.modalSection.description.trim()) {
      this.parsedContent = [];
      return;
    }

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = this.modalSection.description;

    const parsed: ParsedSection[] = [];
    const allNodes = Array.from(tempDiv.childNodes);
    const allElements = allNodes.filter(node => node.nodeType === Node.ELEMENT_NODE) as HTMLElement[];

    let currentTitle: string = '';
    let currentContent: string[] = [];
    let currentSubsections: Array<{ subtitle: string; points: string[] }> = [];
    let currentSubtitle: string = '';
    let currentPoints: string[] = [];
    let hasHeading1: boolean = false;

    for (let i = 0; i < allElements.length; i++) {
      const element = allElements[i];
      const elementTag = element.tagName.toUpperCase();
      const elementDataHeading = element.getAttribute('data-heading');
      const elementDataValue = element.getAttribute('data-value');
      const elementClasses = element.classList?.toString().toLowerCase() || '';

      const isHeading2 = elementTag === 'H2' || 
                         elementDataHeading === '2' ||
                         elementClasses.includes('ql-size-h2') ||
                         elementClasses.includes('ql-header-2');
      
      const isAnyHeading = (elementTag === 'H1' || elementTag === 'H3' || elementTag === 'H4' || 
                            elementTag === 'H5' || elementTag === 'H6');
      
      const isActualH1 = elementTag === 'H1' || 
                         elementTag === 'H3' ||
                         elementDataHeading === '1' || elementDataHeading === '3' ||
                         elementDataValue === '1' || elementDataValue === '3' ||
                         elementClasses.includes('ql-size-h1') || elementClasses.includes('ql-size-h3') ||
                         elementClasses.includes('ql-header-1') || elementClasses.includes('ql-header-3') ||
                         (elementClasses.includes('ql-header') && (
                           elementClasses.includes('h1') || elementClasses.includes('h3') ||
                           elementClasses.includes('header-1') || elementClasses.includes('header-3') ||
                           elementDataHeading === '1' || elementDataHeading === '3' ||
                           elementDataValue === '1' || elementDataValue === '3'
                         ));
      
      const isSubtitleHeading = isAnyHeading && !isHeading2;
      
      const isParagraph = elementTag === 'P';
      const isBoldElement = elementTag === 'STRONG' || elementTag === 'B';
      const boldChild = element.querySelector('strong, b') as HTMLElement | null;
      const hasBoldContent = boldChild !== null;
      const entireTextIsBold = isBoldElement || 
                               (isParagraph && hasBoldContent && 
                                element.textContent?.trim() === boldChild?.textContent?.trim());
      
      const hasH1Structure = element.querySelector('h1, [data-heading="1"]') !== null ||
                             element.closest('[data-heading="1"]') !== null;
      
      const isHeading1 = isSubtitleHeading ||
                         hasH1Structure ||
                         (entireTextIsBold && isParagraph && currentTitle && !hasHeading1);

      if (isHeading2) {
        if (currentTitle) {
          if (hasHeading1) {
            if (currentSubtitle) {
              currentSubsections.push({
                subtitle: currentSubtitle,
                points: currentPoints.length > 0 ? [...currentPoints] : []
              });
            }
            if (currentSubsections.length > 0 || currentSubtitle) {
              parsed.push({
                title: currentTitle,
                subsections: currentSubsections.length > 0 ? currentSubsections : 
                            (currentSubtitle ? [{ subtitle: currentSubtitle, points: currentPoints }] : [])
              });
            }
          } else if (currentContent.length > 0) {
            const contentText = currentContent.join(' ').trim();
            if (contentText) {
              parsed.push({
                title: currentTitle,
                content: contentText
              });
            }
          } else {
            parsed.push({
              title: currentTitle,
              content: ''
            });
          }
        }
        currentTitle = element.textContent?.trim() || '';
        currentContent = [];
        currentSubsections = [];
        currentSubtitle = '';
        currentPoints = [];
        hasHeading1 = false;
      } else if (isHeading1 && currentTitle) {
        if (currentSubtitle) {
          currentSubsections.push({
            subtitle: currentSubtitle,
            points: currentPoints.length > 0 ? [...currentPoints] : []
          });
        }
        hasHeading1 = true;
        let h1Text = element.textContent?.trim() || element.innerText?.trim() || '';
        if (elementTag === 'P') {
          const boldChild = element.querySelector('strong, b') as HTMLElement | null;
          if (boldChild) {
            h1Text = boldChild.textContent?.trim() || boldChild.innerText?.trim() || h1Text;
          }
        }
        h1Text = h1Text.replace(/\s+/g, ' ').trim();
        currentSubtitle = h1Text;
        currentPoints = [];
      } else if (currentTitle) {
        if (hasHeading1 && currentSubtitle) {
          const isList = elementTag === 'UL' || elementTag === 'OL' || elementClasses.includes('ql-list');
          const isListItem = elementTag === 'LI';
          const isParagraph = elementTag === 'P';
          
          if (isListItem) {
            const pointText = element.textContent?.trim() || '';
            if (pointText) {
              currentPoints.push(pointText);
            }
          } else if (isList) {
            const listItems = Array.from(element.querySelectorAll('li'));
            listItems.forEach(li => {
              const itemText = li.textContent?.trim() || '';
              if (itemText) {
                currentPoints.push(itemText);
              }
            });
          } else if (isParagraph) {
            const paragraphText = element.textContent?.trim() || '';
            if (paragraphText) {
              currentPoints.push(paragraphText);
            }
          }
        } else {
          const isList = elementTag === 'UL' || elementTag === 'OL' || elementClasses.includes('ql-list');
          const isListItem = elementTag === 'LI';
          const boldChild = isParagraph ? element.querySelector('strong, b') as HTMLElement | null : null;
          let isEntirelyBold = isBoldElement;
          if (isParagraph && boldChild) {
            const boldText = boldChild.textContent?.trim() || '';
            const paraText = element.textContent?.trim() || '';
            isEntirelyBold = paraText === boldText || 
                            (boldText.length > 0 && paraText.length - boldText.length < 5);
          }
          
          // Check if paragraph has only one child which is bold
          const children = Array.from(element.children);
          if (children.length === 1 && (children[0].tagName === 'STRONG' || children[0].tagName === 'B')) {
            isEntirelyBold = true;
          }
          
          // Look ahead to see if next elements contain bullets (indicates Use Case 2)
          let hasBulletsAhead = false;
          if (i < allElements.length - 1) {
            for (let j = i + 1; j < Math.min(i + 6, allElements.length); j++) {
              const nextEl = allElements[j];
              const nextTag = nextEl.tagName.toUpperCase();
              const nextClasses = nextEl.classList?.toString().toLowerCase() || '';
              
              if (nextTag === 'UL' || nextTag === 'OL' || nextTag === 'LI' ||
                  nextClasses.includes('ql-list') ||
                  nextEl.querySelector('ul, ol, li') !== null ||
                  nextEl.querySelector('.ql-list') !== null) {
                hasBulletsAhead = true;
                break;
              }
            }
          }
          
          // If we find a bold paragraph followed by bullets, treat it as Heading 1 (Use Case 2)
          if ((isEntirelyBold || isBoldElement) && hasBulletsAhead && !hasHeading1) {
            hasHeading1 = true;
            if (isBoldElement) {
              currentSubtitle = element.textContent?.trim() || '';
            } else if (boldChild) {
              currentSubtitle = boldChild.textContent?.trim() || '';
            } else {
              currentSubtitle = element.textContent?.trim() || '';
            }
            currentPoints = [];
            continue;
          } else if (isList || isListItem) {
            // If we encounter bullets, we're likely in Use Case 2
            // Check if we should have detected a bold paragraph as H1
            if (!hasHeading1 && currentTitle) {
              // Look back to see if previous element was a bold paragraph (should be H1)
              if (i > 0) {
                const prevEl = allElements[i - 1];
                const prevTag = prevEl.tagName.toUpperCase();
                const prevIsParagraph = prevTag === 'P';
                const prevBoldChild = prevIsParagraph ? prevEl.querySelector('strong, b') as HTMLElement | null : null;
                const prevEntirelyBold = prevIsParagraph && prevBoldChild && 
                                        prevEl.textContent?.trim() === prevBoldChild.textContent?.trim();
                
                if (prevEntirelyBold) {
                  hasHeading1 = true;
                  if (prevBoldChild) {
                    currentSubtitle = prevBoldChild.textContent?.trim() || prevEl.textContent?.trim() || '';
                  } else {
                    currentSubtitle = prevEl.textContent?.trim() || '';
                  }
                  currentPoints = [];
                }
              }
            }
            
            // Now process bullets as points
            if (hasHeading1 && currentSubtitle) {
              if (isListItem) {
                const pointText = element.textContent?.trim() || '';
                if (pointText) {
                  currentPoints.push(pointText);
                }
              } else if (isList) {
                const listItems = Array.from(element.querySelectorAll('li'));
                listItems.forEach(li => {
                  const itemText = li.textContent?.trim() || '';
                  if (itemText) {
                    currentPoints.push(itemText);
                  }
                });
              }
            }
          } else if (isParagraph && !hasHeading1) {
            // Use Case 1: Simple format - only paragraph text, no bullets, no Heading 2
            const containsList = element.querySelector('ul, ol, li') !== null ||
                                element.querySelector('.ql-list') !== null;
            const containsHeading2 = element.querySelector('h2') !== null ||
                                     element.querySelector('[data-heading="2"]') !== null ||
                                     element.querySelector('.ql-size-h2, .ql-header-2') !== null;
            const containsHeading1 = element.querySelector('h1') !== null ||
                                     element.querySelector('[data-heading="1"]') !== null ||
                                     element.querySelector('.ql-size-h1, .ql-header-1') !== null;
            
            // Only include paragraph if it doesn't contain lists or headings and is not entirely bold
            if (!containsList && !containsHeading2 && !containsHeading1 && !isEntirelyBold) {
              const textContent = element.textContent?.trim() || '';
              if (textContent) {
                currentContent.push(textContent);
              }
            }
          } else if (isParagraph && hasHeading1 && currentSubtitle) {
            // We're in Use Case 2 mode and found a paragraph - add to points
            const paragraphText = element.textContent?.trim() || '';
            if (paragraphText && !isEntirelyBold) {
              currentPoints.push(paragraphText);
            }
          }
        }
      }
    }

    if (currentTitle) {
      if (hasHeading1) {
        if (currentSubtitle) {
          currentSubsections.push({
            subtitle: currentSubtitle,
            points: currentPoints.length > 0 ? [...currentPoints] : []
          });
        }
        if (currentSubsections.length > 0 || currentSubtitle) {
          const subsectionsToSave = currentSubsections.length > 0 ? currentSubsections : 
                                    (currentSubtitle ? [{ subtitle: currentSubtitle, points: currentPoints }] : []);
          parsed.push({
            title: currentTitle,
            subsections: subsectionsToSave
          });
        }
      } else if (currentContent.length > 0) {
        const contentText = currentContent.join(' ').trim();
        if (contentText) {
          parsed.push({
            title: currentTitle,
            content: contentText
          });
        }
      } else {
        parsed.push({
          title: currentTitle,
          content: ''
        });
      }
    }

    this.parsedContent = parsed;
  }

  updatePreviewData(): void {
    if (this.parsedContent && this.parsedContent.length > 0) {
      const sectionsForPreview = this.parsedContent.map(section => {
        if (section.subsections && section.subsections.length > 0) {
          return {
            title: section.title,
            subsections: section.subsections.map(sub => ({
              subtitle: sub.subtitle,
              points: sub.points || []
            }))
          };
        } else {
          return {
            title: section.title,
            content: section.content || ''
          };
        }
      });

      this.previewDataForEmbedded = {
        title: this.metadata?.title || 'Use Case Preview',
        description: this.metadata?.description || '',
        shortDescription: this.metadata?.shortDescription || '',
        accessPolicy: this.metadata?.accessPolicy || '',
        industries: this.metadata?.industries || [],
        organisationTypes: this.metadata?.organisationTypes || [],
        sections: sectionsForPreview,
        isPreview: true,
        createdAt: new Date().toISOString()
      };
    }
  }

  findFormatFlaws(htmlContent: string): Array<{ index: number; element: string; message: string }> {
    const flaws: Array<{ index: number; element: string; message: string }> = [];
    if (!htmlContent || !htmlContent.trim()) {
      return flaws;
    }

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;

    const allowedTopLevelElements = new Set(['H2', 'H3', 'P', 'UL', 'DIV']);
    const allowedNestedElements = new Set(['STRONG', 'B', 'LI', 'BR', 'SPAN']);
    const disallowedElements = new Set([
      'H1', 'H4', 'H5', 'H6', 'OL', 'TABLE', 'TR', 'TD', 'TH',
      'IMG', 'A', 'BLOCKQUOTE', 'HR', 'PRE', 'CODE', 'VIDEO', 'AUDIO', 'IFRAME'
    ]);

    const getFlawMessage = (tagName: string, isTopLevel: boolean): string => {
      if (disallowedElements.has(tagName)) {
        const suggestions: Record<string, string> = {
          H1: 'Use Heading 2 or Heading 3 instead',
          H4: 'Use Heading 2 or Heading 3 instead',
          H5: 'Use Heading 2 or Heading 3 instead',
          H6: 'Use Heading 2 or Heading 3 instead',
          OL: 'Use bullet list instead',
          A: 'Links are not allowed',
          IMG: 'Images are not allowed'
        };
        return suggestions[tagName] || 'Element not allowed';
      }
      if (isTopLevel && !allowedTopLevelElements.has(tagName)) {
        return 'Use Heading 2, Heading 3, Paragraph, or Bullet list';
      }
      return 'Element not allowed in this context';
    };

    const collectFlaws = (element: HTMLElement, blockIndex: number, isTopLevel: boolean): void => {
      const tagName = element.tagName.toUpperCase();
      if (tagName === 'DIV' || tagName === 'SPAN') {
        if (isTopLevel && element.children.length === 0) {
          return;
        }
        const children = Array.from(element.children) as HTMLElement[];
        children.forEach((child, i) => collectFlaws(child, blockIndex, false));
        return;
      }
      if (disallowedElements.has(tagName)) {
        flaws.push({ index: blockIndex, element: `<${tagName}>`, message: getFlawMessage(tagName, isTopLevel) });
      } else if (isTopLevel && !allowedTopLevelElements.has(tagName)) {
        flaws.push({ index: blockIndex, element: `<${tagName}>`, message: getFlawMessage(tagName, isTopLevel) });
      } else if (!isTopLevel && !allowedNestedElements.has(tagName) && !allowedTopLevelElements.has(tagName)) {
        flaws.push({ index: blockIndex, element: `<${tagName}>`, message: getFlawMessage(tagName, isTopLevel) });
      }
      const children = Array.from(element.children) as HTMLElement[];
      children.forEach((child) => collectFlaws(child, blockIndex, false));
    };

    const topLevelElements = Array.from(tempDiv.children) as HTMLElement[];
    topLevelElements.forEach((el, i) => collectFlaws(el as HTMLElement, i, true));

    // Content must start with Heading 2 (Title)
    const isHeading2 = (el: HTMLElement): boolean => {
      const tag = el.tagName.toUpperCase();
      const classes = el.classList?.toString().toLowerCase() || '';
      const dataHeading = el.getAttribute('data-heading');
      return tag === 'H2' || classes.includes('ql-header-2') || classes.includes('ql-size-h2') || dataHeading === '2';
    };
    const getFirstBlock = (el: HTMLElement): HTMLElement | null => {
      const tag = el.tagName.toUpperCase();
      if (tag === 'DIV' || tag === 'SPAN') {
        const first = el.querySelector('h2, h3, p, ul, [class*="ql-header"], [data-heading]');
        return (first as HTMLElement) || el;
      }
      return el;
    };
    const firstEl = topLevelElements[0] as HTMLElement | undefined;
    if (firstEl) {
      const firstBlock = getFirstBlock(firstEl) ?? firstEl;
      if (!isHeading2(firstBlock)) {
        flaws.unshift({ index: 0, element: 'First block', message: 'Content must start with a Title (Heading 2). Use the style dropdown to select "Title".' });
      }
    }

    // Basic validation: at least one Title (H2) and one Paragraph (P or list) must be present
    const walkAll = (parent: HTMLElement, fn: (el: HTMLElement) => void): void => {
      const walk = (el: HTMLElement) => {
        fn(el);
        if (el.children.length) {
          Array.from(el.children).forEach((c) => walk(c as HTMLElement));
        }
      };
      Array.from(parent.children).forEach((c) => walk(c as HTMLElement));
    };
    let hasH2 = false;
    let hasParagraph = false;
    const isHeading3 = (el: HTMLElement): boolean => {
      const tag = el.tagName.toUpperCase();
      const classes = el.classList?.toString().toLowerCase() || '';
      const dataHeading = el.getAttribute('data-heading');
      return tag === 'H3' || classes.includes('ql-header-3') || classes.includes('ql-size-h3') || dataHeading === '3';
    };
    walkAll(tempDiv, (el) => {
      if (isHeading2(el)) hasH2 = true;
      const tag = el.tagName.toUpperCase();
      const isNormalParagraph = tag === 'P' && !isHeading2(el) && !isHeading3(el);
      const isListItem = tag === 'LI' && el.closest('ul');
      if (isNormalParagraph || isListItem) hasParagraph = true;
    });
    if (!hasH2) {
      flaws.unshift({ index: 0, element: 'Content', message: 'At least one Title (Heading 2) is required.' });
    }
    if (!hasParagraph) {
      flaws.unshift({ index: 0, element: 'Content', message: 'At least one Paragraph (Normal) or bullet list is required.' });
    }

    return flaws;
  }

  onEditorInit(event: any): void {
    if (event.editor) {
      this.quillInstance = event.editor;
      const editorElement = event.editor.root;
      if (editorElement) {
        editorElement.setAttribute('dir', 'ltr');
        editorElement.style.direction = 'ltr';
        editorElement.style.textAlign = 'left';
      }

      const clipboard = event.editor.getModule('clipboard');
      if (clipboard) {
        const originalPaste = clipboard.dangerouslyPasteHTML.bind(clipboard);
        clipboard.dangerouslyPasteHTML = (html: string, source?: string) => {
          const convertedHtml = this.convertOrderedToUnorderedLists(html);
          return originalPaste(convertedHtml, source);
        };
      }

      // Header dropdown labels are set via CSS (::before content) in component styles
    }
  }

  onEditorTextChange(event?: any): void {
    // Clear previous flaws when user edits (validation runs only on Save)
    if (this.editorFormatFlaws.length > 0) {
      this.editorFormatFlaws = [];
    }
    // Use event.htmlValue when available (fires with current content) for accurate validation
    const html = event?.htmlValue ?? this.modalSection?.description ?? '';
    this.updateCanSaveContent(html);
    this.cdr.detectChanges();
    // Re-check after ngModel updates (handles async binding)
    setTimeout(() => this.updateCanSaveContent(), 0);
  }

  private updateCanSaveContent(html?: string): void {
    const content = html ?? this.modalSection?.description ?? '';
    this.canSaveContent = this.hasTitleAndParagraph(content);
  }

  private hasTitleAndParagraph(htmlContent: string): boolean {
    if (!htmlContent?.trim()) return false;
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    const isHeading2 = (el: HTMLElement): boolean => {
      const tag = el.tagName.toUpperCase();
      const classes = el.classList?.toString().toLowerCase() || '';
      const dataHeading = el.getAttribute('data-heading');
      const dataValue = el.getAttribute('data-value');
      return tag === 'H2' ||
        classes.includes('ql-header-2') || classes.includes('ql-size-h2') ||
        dataHeading === '2' || dataValue === '2';
    };
    const isHeading3 = (el: HTMLElement): boolean => {
      const tag = el.tagName.toUpperCase();
      const classes = el.classList?.toString().toLowerCase() || '';
      const dataHeading = el.getAttribute('data-heading');
      const dataValue = el.getAttribute('data-value');
      return tag === 'H3' ||
        classes.includes('ql-header-3') || classes.includes('ql-size-h3') ||
        dataHeading === '3' || dataValue === '3';
    };
    let hasH2 = false;
    let hasParagraph = false;
    const walk = (el: HTMLElement) => {
      if (isHeading2(el)) hasH2 = true;
      const tag = el.tagName.toUpperCase();
      const isNormalParagraph = tag === 'P' && !isHeading2(el) && !isHeading3(el);
      const isListItem = tag === 'LI' && el.closest('ul');
      if (isNormalParagraph || isListItem) hasParagraph = true;
      Array.from(el.children).forEach((c) => walk(c as HTMLElement));
    };
    Array.from(tempDiv.children).forEach((c) => walk(c as HTMLElement));
    // Fallback: check for header/paragraph via querySelector (catches nested or alternate structures)
    if (!hasH2 && tempDiv.querySelector('h2, [data-heading="2"], [data-value="2"], .ql-header-2, .ql-size-h2')) {
      hasH2 = true;
    }
    if (!hasParagraph && tempDiv.querySelector('p, ul li')) {
      const hasNormalP = Array.from(tempDiv.querySelectorAll('p')).some((p) => {
        const el = p as HTMLElement;
        return !isHeading2(el) && !isHeading3(el);
      });
      if (hasNormalP || tempDiv.querySelector('ul li')) hasParagraph = true;
    }
    return hasH2 && hasParagraph;
  }

  validate(): boolean {
    const errors: { [key: string]: string } = {};
    if (!this.sections || this.sections.length === 0) {
      errors['sections'] = 'At least one section is required';
    }
    this.validationErrorsChange.emit(errors);
    return Object.keys(errors).length === 0;
  }
}
